import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  limit,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/core/firebase/client'
import {
  AbuseReportSchema,
  ABUSE_DETAIL_MAX,
  type AbuseReport,
  type AbuseReportType,
} from '@/core/models/abuse-report'
import { logger } from '@upadelmatch/core'

// `abuse_reports` — see the model for why a player may only ASSERT.
//
// There is no "read my own reports" call here on purpose. Filing is
// one-directional: reports name people and quote accusations, so the rules give
// `get`/`list` to operators alone, and a client function that pretended
// otherwise would just fail at the rules and confuse whoever wrote it.
const COL = 'abuse_reports'

/**
 * File a report. Returns the new id.
 *
 * The caller supplies `reporterUid` from the live auth session rather than this
 * module reading it, so a component can never file on a stale uid after a
 * re-login — the rules reject a mismatch anyway, and matching the rule at the
 * call site turns that into a bug the developer sees rather than a denial the
 * player sees.
 *
 * `detail` is truncated to the same ceiling the rules enforce. Sending 700
 * characters to be refused by Firestore is a worse experience than sending 600
 * that land, and the two limits are kept in step deliberately.
 */
export async function fileAbuseReport(args: {
  reporterUid: string
  reporterName: string | null
  reportedUid: string
  reportedName: string | null
  type: AbuseReportType
  matchId?: string | null
  detail: string
}): Promise<string> {
  const ref = await addDoc(collection(db, COL), {
    reporterUid: args.reporterUid,
    reporterName: args.reporterName,
    reportedUid: args.reportedUid,
    reportedName: args.reportedName,
    type: args.type,
    matchId: args.matchId ?? null,
    detail: args.detail.trim().slice(0, ABUSE_DETAIL_MAX),
    status: 'open',
    resolution: null,
    resolvedByUid: null,
    resolvedAt: null,
    createdAt: serverTimestamp(),
  })
  return ref.id
}

/** The operator queue. Admin-only at the rules; a non-admin call is denied. */
export async function listAbuseReports(max = 100): Promise<AbuseReport[]> {
  const snap = await getDocs(
    query(collection(db, COL), orderBy('createdAt', 'desc'), limit(max)),
  )
  const out: AbuseReport[] = []
  for (const d of snap.docs) {
    const parsed = AbuseReportSchema.safeParse({ id: d.id, ...d.data() })
    if (parsed.success) out.push(parsed.data)
    // A malformed report must not blank the whole queue — the operator needs
    // the other nine. Same reasoning as the lenient place rows.
    else logger.w('[abuse] unparseable report skipped', d.id, parsed.error.issues[0]?.message)
  }
  return out
}

/**
 * Resolve — through the Cloud Function, never directly.
 *
 * Upholding a disputed no-show has to write `players_public`, which is
 * `allow write: if false` for every client. The CF re-checks the operator
 * server-side and does the whole thing in one transaction, so a double-tap
 * cannot give the same player two no-shows back.
 */
export async function resolveAbuseReport(args: {
  reportId: string
  status: 'upheld' | 'dismissed'
  resolution: string
  disableAccount?: boolean
}): Promise<{ noShowCleared: boolean; accountDisabled: boolean }> {
  const call = httpsCallable<
    typeof args,
    { ok: boolean; noShowCleared: boolean; accountDisabled: boolean }
  >(functions, 'resolve_abuse_report')
  const res = await call(args)
  return {
    noShowCleared: Boolean(res.data?.noShowCleared),
    accountDisabled: Boolean(res.data?.accountDisabled),
  }
}
